import { useMemo, type ReactNode } from 'react'
import { Toast as BaseToast } from '@base-ui/react/toast'
import type { ToastTone } from './tones'

export interface ToastData {
  icon?: ReactNode
}

/** The toast an action belongs to, so the action can rewrite it in place instead of spawning another. */
export interface ToastHandle {
  id: string
  /** Changes the toast where it stands and restarts its timer. Fields left out stay as they were. */
  update: (options: Partial<ToastOptions>) => void
  close: () => void
}

export interface ToastActionOptions {
  /** Usually a word; a node when the label draws more than text. */
  label: ReactNode
  /** Merged onto the button, for a label that needs it positioned (a fill behind the word, say). */
  className?: string
  /**
   * Runs on press. Without a call to `toast.update` the toast closes. Return a promise and the toast
   * stays with the button spinning until it settles: then it closes, or shows the error if it rejected.
   */
  onClick?: (toast: ToastHandle) => void | Promise<unknown>
}

export interface ToastOptions {
  message: ReactNode
  tone?: ToastTone
  /** Replaces the tone's default glyph. */
  icon?: ReactNode
  /** Pass `undefined` in an update to drop the action once it has done its job. */
  action?: ToastActionOptions
  /** Milliseconds before it dismisses itself; 0 keeps it until closed. Defaults to the provider's 5000. */
  timeout?: number
}

type Manager = Pick<ReturnType<typeof BaseToast.useToastManager<ToastData>>, 'add' | 'update' | 'close'>
type UpdateOptions = Parameters<Manager['update']>[1]

export interface ShowToast {
  /** Shows a toast and returns its id. */
  (options: ToastOptions): string
  update: (id: string, options: Partial<ToastOptions>) => void
  close: (id?: string) => void
}

let lastId = 0
function nextId(): string {
  lastId += 1
  return `toast-${lastId}`
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Something went wrong.'
}

/** Shows, updates and closes toasts through one Base UI manager; `useToast` binds the provider's. */
function toasterFor(manager: Manager): ShowToast {
  const actionProps = (id: string, action: ToastActionOptions, loading = false) => ({
    children: action.label,
    className: action.className,
    loading,
    onClick: () => runAction(id, action),
  })

  // Every field the caller named is written, even as undefined, so `action: undefined` clears the action and
  // `timeout` restarts the countdown on each update.
  const toBase = (id: string, options: Partial<ToastOptions>): UpdateOptions => {
    const base: UpdateOptions = {}
    if ('message' in options) base.description = options.message
    if ('tone' in options) base.type = options.tone
    if ('icon' in options) base.data = options.icon === undefined ? undefined : { icon: options.icon }
    if ('action' in options) base.actionProps = options.action ? actionProps(id, options.action) : undefined
    base.timeout = options.timeout
    return base
  }

  const update = (id: string, options: Partial<ToastOptions>) => manager.update(id, toBase(id, options))

  const runAction = (id: string, action: ToastActionOptions) => {
    let handled = false
    const handle: ToastHandle = {
      id,
      update: (options) => {
        handled = true
        update(id, options)
      },
      close: () => {
        handled = true
        manager.close(id)
      },
    }
    const result = action.onClick?.(handle)
    if (!(result instanceof Promise)) {
      if (!handled) manager.close(id)
      return
    }
    // Held open, the button spinning, until the work settles.
    manager.update(id, { actionProps: actionProps(id, action, true), timeout: 0 })
    result.then(
      () => {
        if (!handled) manager.close(id)
      },
      (error: unknown) =>
        update(id, { message: errorMessage(error), tone: 'danger', icon: undefined, action: undefined }),
    )
  }

  const show = (options: ToastOptions) => {
    const id = nextId()
    manager.add({ id, ...toBase(id, options) })
    return id
  }

  return Object.assign(show, { update, close: manager.close })
}

/**
 * `const toast = useToast(); const id = toast({ message: 'Member removed', action: { label: 'Undo', onClick } })`.
 * `toast.update(id, { message: 'Member restored' })` rewrites that toast where it stands.
 */
export function useToast(): ShowToast {
  const { add, update, close } = BaseToast.useToastManager<ToastData>()
  return useMemo(() => toasterFor({ add, update, close }), [add, update, close])
}

export { toasterFor }
